import { motion } from 'framer-motion';
import {
  BriefcaseIcon,
  ChartBarIcon,
  LightBulbIcon,
  PencilSquareIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { ConsultingPageSEO } from '../../seo/seo';

function ConsultingHero() {
  return (
    <section className="relative overflow-hidden px-6 pb-32 pt-40">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(99,102,241,0.15),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-6xl text-left text-gray-800">
        {/* Caption */}
        <span className="animate-fadeUp mb-4 block text-sm font-medium tracking-wide text-indigo-500">
          Expert Consulting Services
        </span>

        <h1 className="mb-8 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          <span className="animate-fadeUp block dark:text-gray-100">Business Transformation</span>
          <span className="animate-fadeUp animate-gradientFlow mt-2 block bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent delay-150">
            Designed to Deliver Results
          </span>
        </h1>

        <p className="animate-fadeUp max-w-2xl text-base leading-relaxed delay-300 sm:text-lg dark:text-gray-100/90">
          We provide actionable insights and strategic guidance that help businesses scale
          efficiently, optimize operations, and leverage technology for lasting impact.
        </p>

        {/* Key Highlights */}
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:gap-8">
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <BriefcaseIcon className="h-6 w-6 text-indigo-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Strategic Planning
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <ChartBarIcon className="h-6 w-6 text-purple-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Data-Driven Insights
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <LightBulbIcon className="h-6 w-6 text-pink-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Innovative Solutions
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
// What You Get
function WhatYouGet() {
  const items = [
    {
      title: 'Strategy & Planning',
      desc: 'Tailored roadmaps to align AI and software investments with business goals.',
      icon: PencilSquareIcon,
      gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    },
    {
      title: 'Process Optimization',
      desc: 'We streamline workflows to boost efficiency and reduce operational friction.',
      icon: ChartBarIcon,
      gradient: 'from-purple-500 via-pink-500 to-red-500',
    },
    {
      title: 'Execution Support',
      desc: 'Guidance and hands-on support to ensure strategies translate into measurable results.',
      icon: RocketLaunchIcon,
      gradient: 'from-blue-500 via-indigo-500 to-purple-500',
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-32">
      {/* Heading */}
      <motion.div
        className="mb-16 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <span className="text-sm font-semibold uppercase tracking-wide text-indigo-500">
          What You Get
        </span>
        <h2 className="mt-2 text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100">
          How We Help Your Business Grow
        </h2>
      </motion.div>

      {/* Grid Cards */}
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              className="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white/80 p-8 shadow-lg backdrop-blur-xl transition-transform hover:scale-105 hover:shadow-2xl dark:border-white/10 dark:bg-gray-900/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              viewport={{ once: true }}
            >
              {/* Gradient Icon Circle */}
              <div
                className={`mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br ${item.gradient} text-white shadow-lg transition-transform group-hover:scale-110`}
              >
                <Icon className="h-7 w-7" />
              </div>

              {/* Title & Description */}
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {item.title}
              </h3>
              <p className="leading-relaxed text-gray-600 dark:text-gray-300">{item.desc}</p>

              {/* Glow Accent */}
              <div
                className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${item.gradient} opacity-30 blur-3xl`}
              />
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

// How We Grow (Timeline)

function WhyAkwid() {
  const points = [
    {
      title: 'Proven Expertise',
      desc: 'Deep AI & software knowledge solving real business problems.',
      color: 'from-indigo-400 via-purple-500 to-pink-500',
    },
    {
      title: 'Scalable Solutions',
      desc: 'Systems built to grow seamlessly with your business.',
      color: 'from-purple-500 via-pink-500 to-red-500',
    },
    {
      title: 'Data-Driven Decisions',
      desc: 'Insights turned into actionable strategies.',
      color: 'from-green-400 via-teal-400 to-blue-500',
    },
    {
      title: 'Global Network',
      desc: 'Access to top-tier experts, tools, and collaborations worldwide.',
      color: 'from-blue-500 via-indigo-500 to-purple-500',
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-32">
      {/* Background gradients */}

      {/* Section Header */}
      <motion.div
        className="mb-20 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <span className="text-[11px] font-semibold uppercase tracking-widest text-indigo-500">
          Why Akwid
        </span>
        <h2 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl dark:text-gray-100">
          Why Choose Us
        </h2>
      </motion.div>

      {/* Timeline */}
      <div className="relative flex flex-col items-center gap-20">
        {/* Vertical line */}
        <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-gradient-to-b from-gray-300 via-gray-200 to-transparent dark:from-gray-700 dark:via-gray-600" />

        {points.map((point, i) => {
          const isLeft = i % 2 === 0;
          return (
            <motion.div
              key={point.title}
              className="relative flex w-full items-center justify-between"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Left Card */}
              <div className={`w-[46%] ${isLeft ? 'block' : 'invisible md:visible'}`}>
                {isLeft && (
                  <div
                    className={`rounded-3xl bg-white/70 p-6 shadow-2xl backdrop-blur-xl dark:bg-gray-900/20`}
                  >
                    <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {point.title}
                    </h3>
                    <p className="text-[13px] text-gray-600 dark:text-gray-300">{point.desc}</p>
                  </div>
                )}
              </div>

              {/* Center Dot */}
              <div className="relative z-10 flex items-center justify-center">
                <div
                  className={`h-8 w-8 rounded-full bg-gradient-to-r ${point.color} flex items-center justify-center font-bold text-white shadow-lg`}
                >
                  {i + 1}
                </div>
              </div>

              {/* Right Card */}
              <div className={`w-[46%] ${!isLeft ? 'block' : 'invisible md:visible'}`}>
                {!isLeft && (
                  <div
                    className={`rounded-3xl bg-white/70 p-6 shadow-2xl backdrop-blur-xl dark:bg-gray-900/20`}
                  >
                    <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {point.title}
                    </h3>
                    <p className="text-[13px] text-gray-600 dark:text-gray-300">{point.desc}</p>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

// === FINAL PAGE ===
export default function Consulting() {
  return (
    <div className="relative">
        <ConsultingPageSEO />
      <ConsultingHero />
      <WhatYouGet />
      <WhyAkwid />
    </div>
  );
}
